
import { Deserializer } from "jsonapi-serializer";
import { INTERNAL_SERVER_ERROR } from "../constants/response-status-codes";
import { AccessGroupOssDevice, AccessGroupOssDeviceWrapper } from "../models/AccessGroupOssDevice";
import { couldNotFindRecordToDelete, genericError } from '../models/GenericError';
import { ListQuery } from "../models/ListQuery";
import { isSuccessStatus } from "../utilities/requestResponseValidator";
import { APIConnector } from "./api/APIConnector";
import { getConstraintViolation, processServerError } from "./utilities/ServiceError";
import { createPagination } from "./utilities/paginationBuilder";
import { createPartialURL, getFilterClause } from "./utilities/urlBuilder";

const apiConnector = new APIConnector();

export const getAccessGroupOssDevices = async (id: string, listParameters?: ListQuery): Promise<AccessGroupOssDeviceWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const withAdditionalFilter = id ? `${filter ? filter.replace('filter=', `filter=accessGroup.id==${id};`) : `&filter=accessGroup.id==${id}`}` : filter;
        const paginationString = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, withAdditionalFilter);
        const mainFields = 'fields[accessGroupOssDevice]=accessGroup,ossDevice';
        const accessGroupFields = 'fields[accessGroup]=agName';
        const ossDeviceFields = "fields[ossDevice]=ossdName,ossdType,ossdDeviceId,site";
        const siteFields = 'fields[site]=siteName';
        const url = `accessGroupOssDevice?include=accessGroup,ossDevice.site${paginationString}&${mainFields}&${accessGroupFields}&${ossDeviceFields}&${siteFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                if (error) {
                    return reject({status: response.status, message: genericError });
                }

                const accessGroupDevices: AccessGroupOssDevice[] = data.map((accessGroupDevice): AccessGroupOssDevice => {
                    return {
                        id: accessGroupDevice['id'],
                        name: accessGroupDevice["oss-device"]["ossd-name"],
                        ossdDeviceId: accessGroupDevice["oss-device"]["ossd-device-id"],
                        ossdNo: accessGroupDevice["oss-device"]["ossd-no"],
                        ossdType: accessGroupDevice["oss-device"]["ossd-type"],
                        siteId: accessGroupDevice["oss-device"]["site"]?.["id"],
                        site: accessGroupDevice["oss-device"]["site"]?.["site-name"],
                        ossDeviceId: accessGroupDevice["oss-device"]["id"],
                        accessGroupId: accessGroupDevice["access-group"]["id"],
                    }
                })
                return resolve({ rows: accessGroupDevices, pagination });
            })
        });
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
}

export const assignUnassignOssDevices = async (toBeAssigned: string[], toBeUnassigned: string[], accessGroupId: string) => {
    
    // Add new assignments
    const newOssLinks = buildToBeSavedRelationship(toBeAssigned, accessGroupId);
    const addedAccessGroups = newOssLinks.map(async (newOssLink) => {
        return await saveOssDeviceRelationship(newOssLink, true)
    })

    // Remove old assignments
    const removedOssDevices = toBeUnassigned.map(async (ossDeviceId) => {
        return await removeOssDeviceRelationship(ossDeviceId, accessGroupId)
    })

    return Promise.all([...addedAccessGroups, ...removedOssDevices]);
}

const buildToBeSavedRelationship = (toBeAssigned: string[], accessGroupId: string) => {
    return toBeAssigned.map((ossDeviceId) => {
        return ({
            data: {
                type: 'accessGroupOssDevice',
                id: "",
                relationships: {
                    ossDevice: {
                        data: {
                            type: 'ossDevice',
                            id: ossDeviceId
                        }
                    },
                    accessGroup: {
                        data: {
                            type: 'accessGroup',
                            id: accessGroupId
                        }
                    }
                }
            }
        })
    })
}

const buildToBeRemovedRelationships = (accessGroupRelationship: AccessGroupOssDevice) => {
    return {
        data: {
            type: 'accessGroupOssDevice',
            id: accessGroupRelationship.id,
            relationships: {
                ossDevice: {
                    data: {
                        type: 'ossDevice',
                        id: accessGroupRelationship.ossDeviceId
                    }
                },
                accessGroup: {
                    data: {
                        type: 'accessGroup',
                        id: accessGroupRelationship.accessGroupId
                    }
                }
            }
        }
    }
}

export const saveOssDeviceRelationship = async (relationship, isNewRelationship = true) => {
    try {
        const url = `accessGroupOssDevice`;
        let response: Response;

        await apiConnector.sendData(url, isNewRelationship, relationship).then(async (data) => {
            response = data;
        }).catch(error => {
            return Promise.reject(processServerError(error));
        });

        return new Promise(async (resolve, reject) => {
            if (response && isSuccessStatus(response.status)) {
                return resolve(true);
            }
            return reject({ status: response.status, message: genericError });
        });
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
}

export const removeOssDeviceRelationship = async (ossDeviceId: string, accessGroupId: string): Promise<boolean> => {
    try {
        // First lookup the Access Group Oss Device
        const relationship = await getOssDeviceGroupRelationship(ossDeviceId, accessGroupId);

        // Found it, let's delete it then.
        if (relationship && relationship.length > 0) {
            const relationshipId = relationship[0].id;
            const url = `accessGroupOssDevice/` + relationshipId;
            let response: Response;

            await apiConnector.deleteData(url, buildToBeRemovedRelationships(relationship[0])).then(async (data) => {
                response = data;
            }).catch(error => {
                return Promise.reject(processServerError(error));
            });

            return new Promise(async (resolve, reject) => {
                if (response && isSuccessStatus(response.status)) {
                    return resolve(true);
                }
                const json = await response.json();
                if (json.errors) {
                    return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
                }
                return reject({ status: response.status, message: genericError });
            });
        }

        // Unable to delete the record. Couldn't find it.
        return Promise.reject({ status: INTERNAL_SERVER_ERROR, message: couldNotFindRecordToDelete });
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
}

export const getOssDeviceGroupRelationship = async (ossDeviceId: String, accessGroupId: String): Promise<AccessGroupOssDevice[]> => {
    try {
        const url = `accessGroupOssDevice?include=ossDevice,accessGroup&filter=ossDevice.id==${ossDeviceId};accessGroup.id==${accessGroupId}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (err: any, results: any[]) => {
                if (err) {
                    return reject(err);
                }
                const ossDevices: AccessGroupOssDevice[] = results.map((relationship): AccessGroupOssDevice => {

                    return {
                        id: relationship['id'],
                        name: relationship["oss-device"]["ossd-name"],
                        ossdDeviceId: relationship["oss-device"]["ossd-device-id"],
                        ossdNo: relationship["oss-device"]["ossd-no"],
                        ossdType: relationship["oss-device"]["ossd-type"],
                        siteId: relationship["oss-device"]["site"]?.["id"],
                        site: relationship["oss-device"]["site"]?.["site-name"],
                        ossDeviceId: relationship["oss-device"]["id"],
                        accessGroupId: relationship["access-group"]["id"],
                    };
                });

                return resolve(ossDevices);
            })
        })
    } catch (error) {
        return Promise.reject(processServerError(error));
    }
}
